import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspaceAPI, advancedAPI, companyAPI } from '../api';
import {
  ArrowLeft, GitCompareArrows, Workflow, Network, Languages, Clock,
  Loader2, Play, Plus, Trash2, ChevronRight, RefreshCw, Eye,
  AlertTriangle, CheckCircle2, XCircle, Zap, ArrowRightLeft,
  Calendar, ToggleLeft, ToggleRight, FileText, Globe, Search
} from 'lucide-react';

export default function AdvancedToolsPage() {
  const { id: workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('compare');
  const [sources, setSources] = useState([]);
  const [denied, setDenied] = useState(false);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        // Check permission first
        const permRes = await companyAPI.myPermissions();
        const perms = permRes.data?.permissions || {};
        setPermissions(perms);
        if (perms.can_use_advanced === false) {
          setDenied(true);
          setLoading(false);
          return;
        }
        const res = await workspaceAPI.get(workspaceId);
        setWorkspace(res.data);
        const allSrc = [
          ...(res.data.documents || []).map(d => ({ id: d.id, title: d.title || d.filename, type: 'document' })),
          ...(res.data.web_sources || []).map(w => ({ id: w.id, title: w.title || w.url, type: 'web' })),
          ...(res.data.text_sources || []).map(t => ({ id: t.id, title: t.title, type: 'text' })),
        ];
        setSources(allSrc);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [workspaceId]);

  const allTabs = [
    { id: 'compare', label: 'Document Comparison', icon: GitCompareArrows, perm: 'can_use_doc_comparison' },
    { id: 'workflows', label: 'Workflows', icon: Workflow, perm: 'can_use_workflows' },
    { id: 'graph', label: 'Knowledge Graph', icon: Network, perm: 'can_use_knowledge_graph' },
    { id: 'language', label: 'Multi-Language', icon: Languages, perm: 'can_use_multi_language' },
    { id: 'schedules', label: 'Scheduled Agents', icon: Clock, perm: 'can_use_scheduled_agents' },
  ];

  const tabs = allTabs.filter(t => permissions[t.perm] !== false);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="advanced-loader">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 text-[#6590FF] animate-spin" />
        <span className="text-sm text-[#6B7280]">Loading Advanced Tools...</span>
      </div>
    </div>
  );

  if (denied) return (
    <div className="h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="advanced-denied">
      <div className="flex flex-col items-center gap-4 text-center">
        <AlertTriangle className="w-10 h-10 text-[#9CA3AF]" />
        <h2 className="text-lg font-bold text-[#111827]">Access Restricted</h2>
        <p className="text-sm text-[#6B7280] max-w-xs">Advanced tools are not available for your role. Contact your admin to enable access.</p>
        <button onClick={() => navigate(`/workspace/${workspaceId}`)} className="h-9 px-5 bg-[#6590FF] text-white text-sm font-semibold rounded-sm">Back to Workspace</button>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#F8F9FA]" data-testid="advanced-tools-page">
      {/* Header */}
      <header className="h-12 border-b border-[#E5E7EB] bg-white flex items-center px-4 gap-3 flex-shrink-0 z-30">
        <button onClick={() => navigate(`/workspace/${workspaceId}`)} className="p-1.5 hover:bg-[#F3F4F6] rounded-sm transition-colors" data-testid="back-to-workspace">
          <ArrowLeft className="w-4 h-4 text-[#4B5563]" />
        </button>
        <div className="h-5 w-px bg-[#E5E7EB]" />
        <span className="text-sm font-bold text-[#111827] truncate" style={{ fontFamily: 'var(--font-heading)' }}>
          {workspace?.name}
        </span>
        <span className="text-xs text-[#9CA3AF] font-medium">/ Advanced Tools</span>
      </header>

      {/* Tab bar */}
      <div className="border-b border-[#E5E7EB] bg-white px-4 flex gap-0 overflow-x-auto flex-shrink-0" data-testid="advanced-tab-bar">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
                active ? 'border-[#6590FF] text-[#6590FF]' : 'border-transparent text-[#6B7280] hover:text-[#111827] hover:border-[#D1D5DB]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'compare' && <DocumentComparisonTab workspaceId={workspaceId} sources={sources} />}
        {activeTab === 'workflows' && <WorkflowsTab workspaceId={workspaceId} />}
        {activeTab === 'graph' && <KnowledgeGraphTab workspaceId={workspaceId} />}
        {activeTab === 'language' && <MultiLanguageTab workspaceId={workspaceId} sources={sources} />}
        {activeTab === 'schedules' && <ScheduledAgentsTab workspaceId={workspaceId} />}
      </div>
    </div>
  );
}

/* ===================== 1. DOCUMENT COMPARISON ===================== */
function DocumentComparisonTab({ workspaceId, sources }) {
  const [sourceA, setSourceA] = useState('');
  const [sourceB, setSourceB] = useState('');
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    advancedAPI.listComparisons(workspaceId).then(r => setHistory(r.data || [])).catch(() => {});
  }, [workspaceId]);

  const runComparison = async () => {
    if (!sourceA || !sourceB) return;
    setComparing(true); setError(''); setResult(null);
    try {
      const res = await advancedAPI.compare(workspaceId, { source_a_id: sourceA, source_b_id: sourceB });
      setResult(res.data);
      advancedAPI.listComparisons(workspaceId).then(r => setHistory(r.data || [])).catch(() => {});
    } catch (e) {
      setError(e.response?.data?.detail || 'Comparison failed');
    }
    setComparing(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="compare-tab">
      {/* Controls */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          Compare Two Documents
        </h3>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Document A</label>
            <select value={sourceA} onChange={e => setSourceA(e.target.value)}
              className="w-full h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] focus:border-[#6590FF] outline-none"
              data-testid="compare-source-a">
              <option value="">Select a source...</option>
              {sources.map(s => <option key={s.id} value={s.id}>{s.title} ({s.type})</option>)}
            </select>
          </div>
          <ArrowRightLeft className="w-5 h-5 text-[#9CA3AF] flex-shrink-0 hidden md:block" />
          <div className="flex-1">
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Document B</label>
            <select value={sourceB} onChange={e => setSourceB(e.target.value)}
              className="w-full h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] focus:border-[#6590FF] outline-none"
              data-testid="compare-source-b">
              <option value="">Select a source...</option>
              {sources.filter(s => s.id !== sourceA).map(s => <option key={s.id} value={s.id}>{s.title} ({s.type})</option>)}
            </select>
          </div>
          <button onClick={runComparison} disabled={!sourceA || !sourceB || comparing}
            className="h-9 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] disabled:bg-[#9CA3AF] text-white text-xs font-semibold rounded-sm transition-colors flex items-center gap-2"
            data-testid="compare-run-btn">
            {comparing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitCompareArrows className="w-3.5 h-3.5" />}
            {comparing ? 'Analyzing...' : 'Compare'}
          </button>
        </div>
        {error && <p className="mt-3 text-xs text-[#C8102E]" data-testid="compare-error">{error}</p>}
      </div>

      {/* Result */}
      {result?.result && <ComparisonResult data={result} />}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
          <h3 className="text-sm font-bold text-[#111827] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Recent Comparisons
          </h3>
          <div className="space-y-2">
            {history.map(h => (
              <button key={h.id} onClick={() => setResult(h)}
                className="w-full text-left p-3 border border-[#E5E7EB] rounded-sm hover:bg-[#F9FAFB] transition-colors flex items-center justify-between"
                data-testid={`comparison-history-${h.id}`}>
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-[#6B7280]" />
                  <span className="text-xs font-medium text-[#111827]">{h.source_a_title}</span>
                  <ArrowRightLeft className="w-3 h-3 text-[#9CA3AF]" />
                  <span className="text-xs font-medium text-[#111827]">{h.source_b_title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={h.status} />
                  <span className="text-[10px] text-[#9CA3AF]">{new Date(h.date_created).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ComparisonResult({ data }) {
  const result = data.result;
  if (!result || result.raw) return null;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="comparison-result">
      {/* Header */}
      <div className="p-5 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>
            Comparison Results
          </h3>
          {result.similarity_score != null && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#6B7280]">Similarity</span>
              <div className="w-24 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div className="h-full bg-[#6590FF] rounded-full transition-all" style={{ width: `${(result.similarity_score * 100)}%` }} />
              </div>
              <span className="text-xs font-bold text-[#6590FF]">{Math.round(result.similarity_score * 100)}%</span>
            </div>
          )}
        </div>
        {result.summary && <p className="text-sm text-[#4B5563] leading-relaxed">{result.summary}</p>}
      </div>

      {/* Sections Diff */}
      {result.sections?.length > 0 && (
        <div className="p-5 border-b border-[#E5E7EB]">
          <h4 className="text-xs font-bold text-[#111827] uppercase tracking-wider mb-3">Section Analysis</h4>
          <div className="space-y-3">
            {result.sections.map((s, i) => (
              <div key={`${s.title || 'sec'}-${i}`} className="border border-[#E5E7EB] rounded-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <DiffStatusIcon status={s.status} />
                  <span className="text-xs font-semibold text-[#111827]">{s.title}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${
                    s.status === 'identical' ? 'bg-[#059669]/10 text-[#059669]' :
                    s.status === 'modified' ? 'bg-[#FFD100]/10 text-[#92700C]' :
                    s.status === 'added' ? 'bg-[#6590FF]/10 text-[#6590FF]' :
                    'bg-[#C8102E]/10 text-[#C8102E]'
                  }`}>{s.status}</span>
                </div>
                {s.status !== 'identical' && (
                  <div className="grid grid-cols-2 divide-x divide-[#E5E7EB]">
                    <div className="p-3">
                      <span className="text-[10px] font-bold text-[#9CA3AF] uppercase block mb-1">Doc A</span>
                      <p className="text-xs text-[#4B5563] leading-relaxed">{s.doc_a || '—'}</p>
                    </div>
                    <div className="p-3">
                      <span className="text-[10px] font-bold text-[#9CA3AF] uppercase block mb-1">Doc B</span>
                      <p className="text-xs text-[#4B5563] leading-relaxed">{s.doc_b || '—'}</p>
                    </div>
                  </div>
                )}
                {s.change_description && (
                  <div className="px-4 py-2 bg-[#F9FAFB] border-t border-[#E5E7EB]">
                    <p className="text-[11px] text-[#6B7280]">{s.change_description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Differences & Similarities */}
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E5E7EB]">
        {result.key_differences?.length > 0 && (
          <div className="p-5">
            <h4 className="text-xs font-bold text-[#C8102E] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Key Differences
            </h4>
            <ul className="space-y-1.5">
              {result.key_differences.map((d, i) => <li key={`kd-${i}-${d?.slice?.(0, 40)}`} className="text-xs text-[#4B5563] pl-3 border-l-2 border-[#C8102E]/30">{d}</li>)}
            </ul>
          </div>
        )}
        {result.key_similarities?.length > 0 && (
          <div className="p-5">
            <h4 className="text-xs font-bold text-[#059669] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Key Similarities
            </h4>
            <ul className="space-y-1.5">
              {result.key_similarities.map((s, i) => <li key={`ks-${i}-${s?.slice?.(0, 40)}`} className="text-xs text-[#4B5563] pl-3 border-l-2 border-[#059669]/30">{s}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Risk Flags */}
      {result.risk_flags?.length > 0 && (
        <div className="p-5 border-t border-[#E5E7EB] bg-[#C8102E]/5">
          <h4 className="text-xs font-bold text-[#C8102E] uppercase tracking-wider mb-2">Risk Flags</h4>
          <ul className="space-y-1">
            {result.risk_flags.map((r, i) => <li key={`rf-${i}-${r?.slice?.(0, 40)}`} className="text-xs text-[#C8102E]/80 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function DiffStatusIcon({ status }) {
  if (status === 'identical') return <CheckCircle2 className="w-3.5 h-3.5 text-[#059669]" />;
  if (status === 'modified') return <RefreshCw className="w-3.5 h-3.5 text-[#92700C]" />;
  if (status === 'added') return <Plus className="w-3.5 h-3.5 text-[#6590FF]" />;
  if (status === 'removed') return <Trash2 className="w-3.5 h-3.5 text-[#C8102E]" />;
  return <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />;
}


/* ===================== 2. WORKFLOWS ===================== */
function WorkflowsTab({ workspaceId }) {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [selectedWf, setSelectedWf] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning] = useState(false);

  const loadWorkflows = useCallback(async () => {
    try {
      const res = await advancedAPI.listWorkflows(workspaceId);
      setWorkflows(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await advancedAPI.createWorkflow(workspaceId, { name: newName, description: newDesc });
      setNewName(''); setNewDesc('');
      loadWorkflows();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const handleRun = async (wfId) => {
    setRunning(true); setRunResult(null);
    try {
      const res = await advancedAPI.runWorkflow(workspaceId, wfId);
      setRunResult(res.data);
    } catch (e) { console.error(e); }
    setRunning(false);
  };

  const handleDelete = async (wfId) => {
    try {
      await advancedAPI.deleteWorkflow(workspaceId, wfId);
      if (selectedWf?.id === wfId) setSelectedWf(null);
      loadWorkflows();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="workflows-tab">
      {/* Create */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          Create Workflow
        </h3>
        <div className="flex flex-col md:flex-row gap-3">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Workflow name"
            className="flex-1 h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] outline-none"
            data-testid="workflow-name-input" />
          <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Description (optional)"
            className="flex-1 h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] outline-none"
            data-testid="workflow-desc-input" />
          <button onClick={handleCreate} disabled={!newName.trim() || creating}
            className="h-9 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] disabled:bg-[#9CA3AF] text-white text-xs font-semibold rounded-sm transition-colors flex items-center gap-2"
            data-testid="workflow-create-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>
      ) : workflows.length === 0 ? (
        <EmptyState icon={Workflow} title="No workflows yet" desc="Create your first automation workflow above." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {workflows.map(wf => (
            <div key={wf.id} className={`bg-white border rounded-sm transition-colors ${selectedWf?.id === wf.id ? 'border-[#6590FF]' : 'border-[#E5E7EB]'}`}
              data-testid={`workflow-card-${wf.id}`}>
              <div className="p-4 flex items-start justify-between border-b border-[#E5E7EB]">
                <div className="cursor-pointer flex-1" onClick={() => setSelectedWf(selectedWf?.id === wf.id ? null : wf)}>
                  <h4 className="text-sm font-bold text-[#111827]">{wf.name}</h4>
                  {wf.description && <p className="text-xs text-[#6B7280] mt-0.5">{wf.description}</p>}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-sm">{wf.trigger_type}</span>
                    <span className="text-[10px] text-[#9CA3AF]">{(wf.steps || []).length} steps</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleRun(wf.id)} disabled={running}
                    className="p-1.5 hover:bg-[#6590FF]/5 text-[#6590FF] rounded-sm transition-colors" title="Run"
                    data-testid={`workflow-run-${wf.id}`}>
                    {running && selectedWf?.id === wf.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button onClick={() => handleDelete(wf.id)}
                    className="p-1.5 hover:bg-[#C8102E]/5 text-[#C8102E] rounded-sm transition-colors" title="Delete"
                    data-testid={`workflow-delete-${wf.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {/* Steps preview */}
              {selectedWf?.id === wf.id && (
                <div className="p-4">
                  <h5 className="text-[10px] font-bold uppercase text-[#9CA3AF] tracking-wider mb-2">Steps</h5>
                  <div className="space-y-2">
                    {(wf.steps || []).map((step, i) => (
                      <div key={step.id || i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#6590FF]/10 text-[#6590FF] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 p-2 border border-[#E5E7EB] rounded-sm bg-[#F9FAFB]">
                          <span className="text-xs font-medium text-[#111827]">{step.label || step.type}</span>
                          <span className="text-[10px] text-[#9CA3AF] ml-2">{step.type}</span>
                        </div>
                        {i < (wf.steps || []).length - 1 && <ChevronRight className="w-3 h-3 text-[#D1D5DB]" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Run Result */}
      {runResult && (
        <div className="bg-white border border-[#059669]/30 rounded-sm p-5" data-testid="workflow-run-result">
          <h3 className="text-sm font-bold text-[#059669] mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Workflow Execution Complete
          </h3>
          <div className="space-y-2">
            {(runResult.results || []).map((r, i) => (
              <div key={`${r.label || 'step'}-${i}`} className="flex items-center gap-3 p-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-sm">
                <StatusBadge status={r.status} />
                <span className="text-xs font-medium text-[#111827]">{r.label}</span>
                <span className="text-[10px] text-[#6B7280] ml-auto">{r.output}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ===================== 3. KNOWLEDGE GRAPH ===================== */
function KnowledgeGraphTab({ workspaceId }) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    advancedAPI.getGraph(workspaceId).then(r => {
      if (r.data?.graph) setGraph(r.data.graph);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [workspaceId]);

  const generate = async () => {
    setGenerating(true); setError('');
    try {
      const res = await advancedAPI.generateGraph(workspaceId);
      if (res.data?.graph) setGraph(res.data.graph);
      else if (res.data?.error) setError(res.data.error);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate graph');
    }
    setGenerating(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="graph-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Knowledge Graph</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">Auto-map relationships between concepts, entities, and documents.</p>
        </div>
        <button onClick={generate} disabled={generating}
          className="h-9 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] disabled:bg-[#9CA3AF] text-white text-xs font-semibold rounded-sm transition-colors flex items-center gap-2"
          data-testid="generate-graph-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Network className="w-3.5 h-3.5" />}
          {generating ? 'Generating...' : graph ? 'Regenerate' : 'Generate Graph'}
        </button>
      </div>
      {error && <p className="text-xs text-[#C8102E]" data-testid="graph-error">{error}</p>}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>
      ) : graph ? (
        <GraphVisualization graph={graph} />
      ) : (
        <EmptyState icon={Network} title="No knowledge graph yet" desc="Generate a graph to visualize relationships across your workspace documents." />
      )}
    </div>
  );
}

function GraphVisualization({ graph }) {
  const svgRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 500 });

  const TYPE_COLORS = {
    concept: '#6590FF', person: '#7C3AED', organization: '#059669',
    regulation: '#C8102E', document: '#92700C', process: '#0891B2', risk: '#DC2626',
  };

  useEffect(() => {
    if (!graph?.nodes?.length) return;
    // Position nodes using a simple circular + force layout
    const w = dimensions.width;
    const h = dimensions.height;
    const cx = w / 2;
    const cy = h / 2;
    const r = Math.min(w, h) * 0.35;

    const positioned = graph.nodes.map((n, i) => {
      const angle = (2 * Math.PI * i) / graph.nodes.length;
      return { ...n, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    });

    // Simple force iteration to spread nodes
    for (let iter = 0; iter < 50; iter++) {
      for (let i = 0; i < positioned.length; i++) {
        for (let j = i + 1; j < positioned.length; j++) {
          const dx = positioned[j].x - positioned[i].x;
          const dy = positioned[j].y - positioned[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const minDist = 80;
          if (dist < minDist) {
            const force = (minDist - dist) / dist * 0.5;
            positioned[i].x -= dx * force;
            positioned[i].y -= dy * force;
            positioned[j].x += dx * force;
            positioned[j].y += dy * force;
          }
        }
        // Attract connected nodes
        for (const edge of (graph.edges || [])) {
          const si = positioned.findIndex(n => n.id === edge.source);
          const ti = positioned.findIndex(n => n.id === edge.target);
          if (si === -1 || ti === -1) continue;
          const dx = positioned[ti].x - positioned[si].x;
          const dy = positioned[ti].y - positioned[si].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          if (dist > 200) {
            const force = (dist - 200) / dist * 0.02;
            positioned[si].x += dx * force;
            positioned[si].y += dy * force;
            positioned[ti].x -= dx * force;
            positioned[ti].y -= dy * force;
          }
        }
        // Keep in bounds
        positioned[i].x = Math.max(40, Math.min(w - 40, positioned[i].x));
        positioned[i].y = Math.max(40, Math.min(h - 40, positioned[i].y));
      }
    }
    setNodes(positioned);
  }, [graph, dimensions]);

  useEffect(() => {
    const resizeObs = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({ width: entry.contentRect.width, height: Math.max(500, entry.contentRect.height) });
      }
    });
    if (svgRef.current?.parentElement) resizeObs.observe(svgRef.current.parentElement);
    return () => resizeObs.disconnect();
  }, []);

  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden" data-testid="graph-visualization">
      {/* Legend */}
      <div className="p-3 border-b border-[#E5E7EB] flex flex-wrap gap-3">
        {Object.entries(TYPE_COLORS).map(([type, color]) => (
          <span key={type} className="flex items-center gap-1.5 text-[10px] text-[#6B7280]">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
            {type}
          </span>
        ))}
      </div>
      {/* SVG */}
      <div className="relative" style={{ height: 500 }}>
        <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
          <defs>
            <marker id="arrowhead" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#D1D5DB" />
            </marker>
          </defs>
          {/* Edges */}
          {(graph.edges || []).map((edge, i) => {
            const src = nodeMap[edge.source];
            const tgt = nodeMap[edge.target];
            if (!src || !tgt) return null;
            const mx = (src.x + tgt.x) / 2;
            const my = (src.y + tgt.y) / 2;
            return (
              <g key={`${edge.source}-${edge.target}-${i}`}>
                <line x1={src.x} y1={src.y} x2={tgt.x} y2={tgt.y}
                  stroke="#D1D5DB" strokeWidth={1.5} markerEnd="url(#arrowhead)" />
                {edge.label && (
                  <text x={mx} y={my - 4} textAnchor="middle" fill="#9CA3AF"
                    fontSize="9" fontFamily="var(--font-body)">
                    {edge.label}
                  </text>
                )}
              </g>
            );
          })}
          {/* Nodes */}
          {nodes.map(node => {
            const color = TYPE_COLORS[node.type] || '#6B7280';
            const isHovered = hoveredNode === node.id;
            return (
              <g key={node.id}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                style={{ cursor: 'pointer' }}>
                <circle cx={node.x} cy={node.y} r={isHovered ? 20 : 14}
                  fill={color} opacity={isHovered ? 1 : 0.85}
                  stroke={isHovered ? '#111827' : 'white'} strokeWidth={isHovered ? 2 : 1.5}
                  style={{ transition: 'r 0.15s, opacity 0.15s' }} />
                <text x={node.x} y={node.y + 28} textAnchor="middle"
                  fill="#111827" fontSize="10" fontWeight="600"
                  fontFamily="var(--font-body)">
                  {node.label?.length > 18 ? node.label.slice(0, 16) + '...' : node.label}
                </text>
                {isHovered && node.description && (
                  <foreignObject x={node.x - 100} y={node.y - 60} width="200" height="40">
                    <div className="bg-[#111827] text-white text-[10px] px-2 py-1 rounded-sm text-center leading-tight">
                      {node.description}
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Clusters */}
      {graph.clusters?.length > 0 && (
        <div className="p-4 border-t border-[#E5E7EB]">
          <h4 className="text-[10px] font-bold uppercase text-[#9CA3AF] tracking-wider mb-2">Clusters</h4>
          <div className="flex flex-wrap gap-2">
            {graph.clusters.map((c) => (
              <span key={c.name} className="text-xs border border-[#E5E7EB] px-3 py-1.5 rounded-sm text-[#4B5563]">
                <strong className="text-[#111827]">{c.name}</strong> — {c.description}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ===================== 4. MULTI-LANGUAGE ===================== */
function MultiLanguageTab({ workspaceId, sources }) {
  const [selectedSource, setSelectedSource] = useState('');
  const [targetLang, setTargetLang] = useState('English');
  const [detecting, setDetecting] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [detectedLang, setDetectedLang] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [error, setError] = useState('');

  const LANGUAGES = [
    'English', 'Arabic', 'Chinese', 'Dutch', 'French', 'German', 'Hindi',
    'Italian', 'Japanese', 'Korean', 'Portuguese', 'Russian', 'Spanish', 'Turkish', 'Urdu'
  ];

  const detectLang = async () => {
    if (!selectedSource) return;
    setDetecting(true); setDetectedLang(null); setError('');
    try {
      const res = await advancedAPI.detectLanguage(workspaceId, selectedSource);
      setDetectedLang(res.data);
    } catch (e) {
      setError('Language detection failed');
    }
    setDetecting(false);
  };

  const translateDoc = async () => {
    if (!selectedSource || !targetLang) return;
    setTranslating(true); setTranslation(null); setError('');
    try {
      const res = await advancedAPI.translate(workspaceId, selectedSource, { target_language: targetLang });
      setTranslation(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Translation failed');
    }
    setTranslating(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="language-tab">
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          Language Detection & Translation
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Select Source Document</label>
            <select value={selectedSource} onChange={e => { setSelectedSource(e.target.value); setDetectedLang(null); setTranslation(null); }}
              className="w-full h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] outline-none"
              data-testid="lang-source-select">
              <option value="">Choose a source...</option>
              {sources.map(s => <option key={s.id} value={s.id}>{s.title} ({s.type})</option>)}
            </select>
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            <button onClick={detectLang} disabled={!selectedSource || detecting}
              className="h-9 px-5 border border-[#E5E7EB] hover:bg-[#F3F4F6] disabled:opacity-50 text-xs font-semibold rounded-sm transition-colors flex items-center gap-2"
              data-testid="detect-lang-btn">
              {detecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              Detect Language
            </button>
            {detectedLang && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#6590FF]/5 border border-[#6590FF]/20 rounded-sm" data-testid="detected-lang-result">
                <Globe className="w-3.5 h-3.5 text-[#6590FF]" />
                <span className="text-xs font-semibold text-[#6590FF]">{detectedLang.language}</span>
                {detectedLang.confidence != null && (
                  <span className="text-[10px] text-[#6B7280]">({Math.round(detectedLang.confidence * 100)}% confidence)</span>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-[#E5E7EB] pt-4">
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Translate To</label>
            <div className="flex flex-col md:flex-row gap-3">
              <select value={targetLang} onChange={e => setTargetLang(e.target.value)}
                className="flex-1 h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] outline-none"
                data-testid="target-lang-select">
                {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <button onClick={translateDoc} disabled={!selectedSource || translating}
                className="h-9 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] disabled:bg-[#9CA3AF] text-white text-xs font-semibold rounded-sm transition-colors flex items-center gap-2"
                data-testid="translate-btn">
                {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
                {translating ? 'Translating...' : 'Translate'}
              </button>
            </div>
          </div>
        </div>
        {error && <p className="mt-3 text-xs text-[#C8102E]" data-testid="lang-error">{error}</p>}
      </div>

      {/* Translation Result */}
      {translation && (
        <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="translation-result">
          <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold text-[#111827]">{translation.title}</h4>
              <span className="text-[10px] text-[#6B7280]">Translated to {translation.target_language}</span>
            </div>
            <button onClick={() => navigator.clipboard.writeText(translation.translated_text)}
              className="h-7 px-3 border border-[#E5E7EB] text-xs font-medium text-[#4B5563] hover:bg-[#F3F4F6] rounded-sm transition-colors"
              data-testid="copy-translation-btn">
              Copy
            </button>
          </div>
          <div className="p-5 max-h-[500px] overflow-y-auto">
            <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap" data-testid="translated-text">
              {translation.translated_text}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


/* ===================== 5. SCHEDULED AGENTS ===================== */
function ScheduledAgentsTab({ workspaceId }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [agentType, setAgentType] = useState('compliance');
  const [frequency, setFrequency] = useState('weekly_monday_9am');
  const [runningId, setRunningId] = useState(null);

  const AGENT_TYPES = [
    { value: 'compliance', label: 'Compliance Checker' },
    { value: 'conflict', label: 'Conflict Detector' },
    { value: 'recommendation', label: 'Recommendation Engine' },
    { value: 'summary', label: 'Auto-Summarizer' },
  ];

  const FREQUENCIES = [
    { value: 'daily_9am', label: 'Daily at 9 AM' },
    { value: 'weekly_monday_9am', label: 'Weekly (Monday 9 AM)' },
    { value: 'weekly_friday_9am', label: 'Weekly (Friday 9 AM)' },
    { value: 'monthly_1st_9am', label: 'Monthly (1st at 9 AM)' },
    { value: 'every_6h', label: 'Every 6 Hours' },
    { value: 'every_12h', label: 'Every 12 Hours' },
  ];

  const loadSchedules = useCallback(async () => {
    try {
      const res = await advancedAPI.listSchedules(workspaceId);
      setSchedules(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await advancedAPI.createSchedule(workspaceId, { agent_type: agentType, frequency_label: frequency });
      loadSchedules();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const handleToggle = async (sched) => {
    try {
      await advancedAPI.updateSchedule(workspaceId, sched.id, { active: !sched.active });
      loadSchedules();
    } catch (e) { console.error(e); }
  };

  const handleRunNow = async (schedId) => {
    setRunningId(schedId);
    try {
      await advancedAPI.runScheduleNow(workspaceId, schedId);
      loadSchedules();
    } catch (e) { console.error(e); }
    setRunningId(null);
  };

  const handleDelete = async (schedId) => {
    try {
      await advancedAPI.deleteSchedule(workspaceId, schedId);
      loadSchedules();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="schedules-tab">
      {/* Create */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
        <h3 className="text-sm font-bold text-[#111827] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          Schedule an Agent Run
        </h3>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Agent Type</label>
            <select value={agentType} onChange={e => setAgentType(e.target.value)}
              className="w-full h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] outline-none"
              data-testid="schedule-agent-select">
              {AGENT_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-[#4B5563] mb-1.5 block">Frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)}
              className="w-full h-9 px-3 border border-[#E5E7EB] rounded-sm text-sm focus:ring-2 focus:ring-[#6590FF] outline-none"
              data-testid="schedule-freq-select">
              {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={handleCreate} disabled={creating}
              className="h-9 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] disabled:bg-[#9CA3AF] text-white text-xs font-semibold rounded-sm transition-colors flex items-center gap-2"
              data-testid="schedule-create-btn">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
              Schedule
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#6590FF] animate-spin" /></div>
      ) : schedules.length === 0 ? (
        <EmptyState icon={Clock} title="No scheduled agents" desc="Create your first scheduled agent run above." />
      ) : (
        <div className="space-y-3">
          {schedules.map(sched => (
            <div key={sched.id} className="bg-white border border-[#E5E7EB] rounded-sm p-4 flex items-center gap-4" data-testid={`schedule-card-${sched.id}`}>
              <button onClick={() => handleToggle(sched)} className="flex-shrink-0" data-testid={`schedule-toggle-${sched.id}`}>
                {sched.active ? (
                  <ToggleRight className="w-8 h-8 text-[#6590FF]" />
                ) : (
                  <ToggleLeft className="w-8 h-8 text-[#9CA3AF]" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#111827] capitalize">{sched.agent_type.replace(/_/g, ' ')}</span>
                  {!sched.active && <span className="text-[10px] bg-[#F3F4F6] text-[#9CA3AF] px-2 py-0.5 rounded-sm font-medium">PAUSED</span>}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] text-[#6B7280]">{sched.frequency_label?.replace(/_/g, ' ')}</span>
                  <span className="text-[10px] text-[#9CA3AF]">Cron: {sched.cron_expression}</span>
                  {sched.last_run_at && <span className="text-[10px] text-[#6B7280]">Last: {new Date(sched.last_run_at).toLocaleString()}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleRunNow(sched.id)} disabled={runningId === sched.id}
                  className="h-7 px-3 border border-[#E5E7EB] text-xs font-medium text-[#6590FF] hover:bg-[#6590FF]/5 rounded-sm transition-colors flex items-center gap-1.5"
                  data-testid={`schedule-run-now-${sched.id}`}>
                  {runningId === sched.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  Run Now
                </button>
                <button onClick={() => handleDelete(sched.id)}
                  className="p-1.5 hover:bg-[#C8102E]/5 text-[#C8102E] rounded-sm transition-colors"
                  data-testid={`schedule-delete-${sched.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ===================== SHARED ===================== */
function StatusBadge({ status }) {
  const styles = {
    completed: 'bg-[#059669]/10 text-[#059669]',
    running: 'bg-[#6590FF]/10 text-[#6590FF]',
    pending: 'bg-[#F3F4F6] text-[#6B7280]',
    processing: 'bg-[#6590FF]/10 text-[#6590FF]',
    failed: 'bg-[#C8102E]/10 text-[#C8102E]',
  };
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}

function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <div className="w-12 h-12 rounded-sm bg-[#F3F4F6] flex items-center justify-center mb-3">
        <Icon className="w-6 h-6 text-[#9CA3AF]" />
      </div>
      <h4 className="text-sm font-bold text-[#111827] mb-1">{title}</h4>
      <p className="text-xs text-[#6B7280] max-w-xs">{desc}</p>
    </div>
  );
}
